import React, { useCallback, useEffect, useState } from "react";

import { useMutationState } from "@tanstack/react-query";

import { addCropExplorationFromTemplate, generatePseudoUserId, updateUserSetting } from "services";

import {
  clearLocalStorageCropExplorerPseudoUserId,
  gentlyTruncateString,
  getLocalStorageCountry,
  getLocalStorageCropExplorerDismissedOnboardingTour,
  getLocalStorageCropExplorerOnboarded,
  getLocalStorageCropExplorerPseudoUserId,
  getLocalStorageHectare,
  getLocalStorageZipCode,
  setLocalStorageCountry,
  setLocalStorageCropExplorerDismissedOnboardingTour,
  setLocalStorageCropExplorerOnboarded,
  setLocalStorageCropExplorerPseudoUserId,
  setLocalStorageHectare,
  setLocalStorageZipCode,
  useAddCropExploration,
  useAddCropExplorationTemplate,
  useGetCropExploration,
  useMigratePseudoCropExplorationsToUser,
  useTranslation,
  useUserContext,
} from "utils";

import { DELETE_CROP_EXPLORATIONS } from "consts";

import { CropExplorationResponse, CropExplorationTemplateResponse } from "models";

import { Form, Icon, Modal, Tabs, TooltipWrapper, Tour, TourProps } from "components";

import { useCropExplorationContext } from "pages/app/crop-explorer/CropExplorationContext";
import { CropExplorerMain } from "pages/app/crop-explorer/components/CropExplorerMain";
import { OnboardingModal } from "pages/app/crop-explorer/components/OnboardingModal";

export type TabItemProp = {
  key: string;
  label: string | React.ReactNode;
  children: React.ReactNode;
  closable: boolean;
};

const DEFAULT_HECTARE_SIZE = 10;
const MAX_DISPLAY_TAB_NAME_LENGTH = 20;

export const CropExplorerTabs = ({ isAdmin }: { isAdmin?: boolean }) => {
  const { t } = useTranslation("cropExplorer");
  const {
    setCropExplorationId,
    tourRefStep1ExampleProjectTab,
    tourRefStep2BauSection,
    tourRefStep3YearSection,
    tourRefStep4AddEditCrop,
    tourRefStep5EmptyProjectTab,
  } = useCropExplorationContext();

  const DEFAULT_PROJECT_NAME = t("project.defaultProjectName");
  const DEFAULT_PROJECT_EXAMPLE_NAME = t("project.defaultProjectExampleName");

  const [activeTab, setActiveTab] = useState<string>();
  const [newName, setNewName] = useState<string>(DEFAULT_PROJECT_NAME);
  const [hideAddTabButton, setHideAddTabButton] = useState<boolean>();
  const [showAddProjectModal, setShowAddProjectModal] = useState<boolean>();
  const [showOnboardingModal, setShowOnboardingModal] = useState<boolean>(false);
  const [tabItems, setTabItems] = useState<TabItemProp[]>([]);
  const [exampleCropExplorationId, setExampleCropExplorationId] = useState<string | null>(null);
  const [openOnboardingTour, setOpenOnboardingTour] = useState<boolean>(false);

  const { mutate: addCropExploration, data: addCropExplorationResponse, isPending: isPendingAddCropExploration } = useAddCropExploration();
  const {
    mutate: addCropExplorationTemplate,
    data: addCropExplorationTemplateResponse,
    isPending: isPendingAddCropExplorationTemplate,
  } = useAddCropExplorationTemplate();
  const {
    mutate: migratePseudoCropExplorationsToUser,
    isPending: isCropExplorationsMigrationPending,
    isSuccess: isCropExplorationsMigrationSuccess,
  } = useMigratePseudoCropExplorationsToUser();
  const { user } = useUserContext();
  const [pseudoUserId, setPseudoUserId] = useState<string | null>(getLocalStorageCropExplorerPseudoUserId());
  const { data: cropExplorations } = useGetCropExploration({ isAdmin: !!isAdmin, user, pseudoUserId });

  const zipCode = getLocalStorageZipCode();
  const country = user?.farm?.country ?? getLocalStorageCountry();
  const hectares = getLocalStorageHectare() ?? DEFAULT_HECTARE_SIZE;
  const cropExplorerOnboardedLocalStorage = getLocalStorageCropExplorerOnboarded();
  const cropExplorerDismissedOnboardingTourLocalStorage = getLocalStorageCropExplorerDismissedOnboardingTour();

  const stepTitle = (text: string) => <h5 className="white-text">{text}</h5>;
  const stepDescription = (text: string) => <p className="paragraph-m white-text">{text}</p>;
  const stepNextButtonArrow = (text: string) => (
    <>
      {text}
      <Icon iconName="tabler/arrow-right" className="icon inline bold" />
    </>
  );

  const steps: TourProps["steps"] = [
    {
      title: stepTitle(t("tour.step1.title")),
      description: stepDescription(t("tour.step1.content")),
      placement: "bottomLeft",
      nextButtonProps: {
        children: stepNextButtonArrow(t("tour.step1.nextButton")),
      },
      target: () => tourRefStep1ExampleProjectTab.current!,
    },
    {
      title: stepTitle(t("tour.step2.title")),
      description: stepDescription(t("tour.step2.content")),
      placement: "topLeft",
      prevButtonProps: { children: t("tour.previousButton") },
      nextButtonProps: {
        children: stepNextButtonArrow(t("tour.step2.nextButton")),
        onClick: () => tourRefStep3YearSection.current!,
      },
      target: () => tourRefStep2BauSection.current!,
    },
    {
      title: stepTitle(t("tour.step3.title")),
      description: stepDescription(t("tour.step3.content")),
      placement: "topLeft",
      prevButtonProps: { children: t("tour.previousButton") },
      nextButtonProps: {
        children: stepNextButtonArrow(t("tour.step3.nextButton")),
      },
      target: () => tourRefStep3YearSection.current!,
    },
    {
      title: stepTitle(t("tour.step4.title")),
      description: stepDescription(t("tour.step4.content")),
      prevButtonProps: { children: t("tour.previousButton") },
      nextButtonProps: {
        children: stepNextButtonArrow(t("tour.step4.nextButton")),
      },
      target: () => tourRefStep4AddEditCrop.current!,
      placement: "right",
    },
    {
      title: stepTitle(t("tour.step5.title")),
      prevButtonProps: {
        children: t("tour.previousButton"),
      },
      nextButtonProps: { children: t("tour.step5.nextButton") },
      target: () => tourRefStep5EmptyProjectTab.current!,
      placement: "bottom",
    },
  ];

  const deleteState = useMutationState({
    filters: { mutationKey: [DELETE_CROP_EXPLORATIONS] },
    select: (mutation) => mutation.state,
  });

  const [form] = Form.useForm();

  const getNextNumber = () => {
    const projectCountWithInitialNames = cropExplorations?.filter((cropExploration) => cropExploration.name.includes(DEFAULT_PROJECT_NAME)).length;
    return projectCountWithInitialNames || 0;
  };

  const getInitialName = () => {
    const nameCount = getNextNumber();
    const nextNumber = nameCount === 0 ? "" : ` 0${nameCount}`;
    setNewName(`${DEFAULT_PROJECT_NAME}${nextNumber}`);
  };

  const handleAddCropExploration = (isAdmin: boolean) => {
    if (isAdmin) {
      addCropExplorationTemplate({ name: newName });
      return;
    }

    if ((!user && !pseudoUserId) || !hectares || !zipCode || !country) {
      setShowAddProjectModal(false);
      setShowOnboardingModal(true);
      return;
    }

    addCropExploration({
      name: newName,
      country,
      zipCode: zipCode as string,
      hectares,
      pseudoUserId,
    });
  };

  const handleAddOrEditTab = (_targetKey: React.MouseEvent | React.KeyboardEvent | string, action: "add" | "remove") => {
    if (action === "add") {
      setShowAddProjectModal(true);
    }
  };

  const onTabChange = (newActiveKey: string) => {
    setActiveTab(newActiveKey);
    setCropExplorationId(newActiveKey);
    if (cropExplorations) {
      const currentCropExploration = cropExplorations.find((cropExploration) => cropExploration.id === newActiveKey);
      if (currentCropExploration && !isAdmin) {
        setLocalStorageCountry((currentCropExploration as CropExplorationResponse).country);
        setLocalStorageZipCode((currentCropExploration as CropExplorationResponse).zipCode);
        setLocalStorageHectare((currentCropExploration as CropExplorationResponse).hectares);
      }
    }
  };

  const getTabTourRef = (index: number) => {
    if (index === 0) return tourRefStep1ExampleProjectTab;
    if (index === 1) return tourRefStep5EmptyProjectTab;
    return undefined;
  };

  const convertCropExplorationToTabItem = (cropExploration: CropExplorationResponse | CropExplorationTemplateResponse, index: number) => ({
    label: (
      <TooltipWrapper
        mRef={getTabTourRef(index)}
        tooltip={cropExploration.name.length > MAX_DISPLAY_TAB_NAME_LENGTH ? { title: cropExploration.name, placement: "top" } : {}}
      >
        {gentlyTruncateString(cropExploration.name, MAX_DISPLAY_TAB_NAME_LENGTH)}
      </TooltipWrapper>
    ),
    children: <CropExplorerMain cropExploration={cropExploration} isAdmin={isAdmin} />,
    key: cropExploration.id,
    closable: false,
  });

  const createEmptyProject = () => {
    if (isAdmin) {
      addCropExplorationTemplate({ name: newName });
      return;
    }

    if ((!user && !pseudoUserId) || !hectares || !zipCode || !country) {
      setShowOnboardingModal(true);
      return;
    }

    addCropExploration({
      name: newName,
      country,
      zipCode,
      hectares,
      pseudoUserId,
    });
  };

  const convertCropExplorationsToTabItems = (cropExplorations: CropExplorationResponse[] | CropExplorationTemplateResponse[]) => {
    if (cropExplorations.length > 0) {
      const newTabItems = cropExplorations.map((cropExploration, index) => convertCropExplorationToTabItem(cropExploration, index));
      setTabItems(newTabItems);
      return;
    }

    // check if a migration from pseudoUser is in progress or if user is not yet onboarded
    if ((user && pseudoUserId) || !cropExplorerOnboardedLocalStorage) return;

    createEmptyProject();
  };

  const getNewTabIndex = (deleteItemKey: string): string | undefined => {
    const newActiveKey = activeTab;
    let lastIndex = -1;
    tabItems?.forEach((tabItem, index) => {
      if (tabItem.key === deleteItemKey) lastIndex = index - 1;
    });

    if (!tabItems?.length || newActiveKey !== deleteItemKey) return newActiveKey;
    if (lastIndex >= 0) return tabItems[lastIndex].key;
    if (tabItems?.length === 1) return tabItems[0].key;

    return tabItems[1].key;
  };

  const handleActiveTabAfterDeletion = () => {
    if (deleteState.length === 0) return;
    if (deleteState[deleteState.length - 1].status === "success") {
      const deleteItemKey = (deleteState[deleteState.length - 1].variables as { id: string; pseudoUserId?: string })?.id;
      const newActiveKey = getNewTabIndex(deleteItemKey);
      setActiveTab(newActiveKey);
      if (newActiveKey) setCropExplorationId(newActiveKey);
    }
  };

  const handleOnboardingModalFinish = async ({ zipCode, country, hectares }: { zipCode: string; country: string; hectares: number }) => {
    if (isAdmin) return;
    if (!user && !pseudoUserId) return;

    if (!cropExplorations || cropExplorations.length === 0) {
      // add Crop Exploration example only if user was not onboarded yet
      if (!cropExplorerOnboardedLocalStorage) {
        const exampleCropExploration = await addCropExplorationFromTemplate({
          name: DEFAULT_PROJECT_EXAMPLE_NAME,
          country,
          zipCode,
          hectares,
          pseudoUserId: getLocalStorageCropExplorerPseudoUserId() || null,
        });
        if (exampleCropExploration.success) setExampleCropExplorationId(exampleCropExploration.id);
      }
    }

    addCropExploration({
      name: newName,
      country,
      zipCode,
      hectares,
      pseudoUserId: getLocalStorageCropExplorerPseudoUserId() || null,
    });
  };

  const handleCloseOnboardingTour = () => {
    setLocalStorageCropExplorerDismissedOnboardingTour(true);
    setOpenOnboardingTour(false);
  };

  const afterAddCropExploration = (isAdmin: boolean = false) => {
    if (isAdmin) {
      if (isPendingAddCropExplorationTemplate) return;
      if (addCropExplorationTemplateResponse?.id) {
        setLocalStorageCropExplorerOnboarded(true);
        setActiveTab(addCropExplorationTemplateResponse.id);
        setCropExplorationId(addCropExplorationTemplateResponse.id);
      }
    }

    if (isPendingAddCropExploration) return;
    if (addCropExplorationResponse?.id) {
      setLocalStorageCropExplorerOnboarded(true);

      // Set the active tab to the CE example after user onboarding
      if (exampleCropExplorationId) {
        setActiveTab(exampleCropExplorationId || addCropExplorationResponse.id);
        setCropExplorationId(exampleCropExplorationId || addCropExplorationResponse.id);
        setExampleCropExplorationId(null);
        return;
      }

      setActiveTab(addCropExplorationResponse.id);
      setCropExplorationId(addCropExplorationResponse.id);
    }
    setShowOnboardingModal(false);
    setShowAddProjectModal(false);
  };

  const afterFetch = () => {
    if (!cropExplorations) return;
    getInitialName();
    convertCropExplorationsToTabItems(cropExplorations);
    setHideAddTabButton(cropExplorations.length > 9);
  };

  const launchOnboardingTour = () => {
    if (isAdmin) return;
    if (!cropExplorations || cropExplorations.length === 0) return;
    if (cropExplorerOnboardedLocalStorage && !cropExplorerDismissedOnboardingTourLocalStorage) {
      setOpenOnboardingTour(true);
    }
  };

  const getPseudoUserLogin = useCallback(async () => {
    const localPseudoUserId = getLocalStorageCropExplorerPseudoUserId();
    if (localPseudoUserId || user) {
      setPseudoUserId(localPseudoUserId);
      return;
    }

    if (!user && !localPseudoUserId) {
      const newPseudoUserId = await generatePseudoUserId();
      setLocalStorageCropExplorerPseudoUserId(newPseudoUserId.pseudoUserId);
      setPseudoUserId(newPseudoUserId.pseudoUserId);
    }
  }, [user]);

  const handlePseudoCropExplorationsMigration = useCallback(() => {
    if (isAdmin) return;
    if (!user || !pseudoUserId) return;

    migratePseudoCropExplorationsToUser(pseudoUserId);
  }, [isAdmin, migratePseudoCropExplorationsToUser, pseudoUserId, user]);

  const syncUserSettingWithLocalCropExplorerState = useCallback(async () => {
    if (user) {
      if (user.cropExplorer?.cropExplorerOnboarded !== cropExplorerOnboardedLocalStorage) {
        await updateUserSetting({
          item: "cropExplorerOnboarded",
          value: !!cropExplorerOnboardedLocalStorage,
        });
      }
      if (user.cropExplorer?.cropExplorerDismissedOnboardingTour !== cropExplorerDismissedOnboardingTourLocalStorage) {
        await updateUserSetting({
          item: "cropExplorerDismissedOnboardingTour",
          value: !!cropExplorerDismissedOnboardingTourLocalStorage,
        });
      }
    }
  }, [cropExplorerOnboardedLocalStorage, cropExplorerDismissedOnboardingTourLocalStorage, user]);

  useEffect(() => {
    syncUserSettingWithLocalCropExplorerState();
  }, [syncUserSettingWithLocalCropExplorerState]);

  useEffect(() => {
    getPseudoUserLogin();
  }, [getPseudoUserLogin]);

  useEffect(() => {
    afterFetch();
  }, [cropExplorations]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    afterAddCropExploration(isAdmin);
  }, [addCropExplorationResponse, isPendingAddCropExploration, addCropExplorationTemplateResponse, isPendingAddCropExplorationTemplate, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    handleActiveTabAfterDeletion();
  }, [deleteState]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    form.setFieldValue("add-name", newName);
  }, [newName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    launchOnboardingTour();
  }, [cropExplorations, cropExplorerDismissedOnboardingTourLocalStorage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAdmin) return;

    if (!cropExplorerOnboardedLocalStorage && !isCropExplorationsMigrationPending) {
      setShowOnboardingModal(true);
    }
  }, [isAdmin, cropExplorerOnboardedLocalStorage, isCropExplorationsMigrationPending]);

  useEffect(() => {
    if (isAdmin) return;
    if (!user || !pseudoUserId) return;

    if (!isCropExplorationsMigrationPending && !isCropExplorationsMigrationSuccess) {
      handlePseudoCropExplorationsMigration();
    }
    if (isCropExplorationsMigrationSuccess) {
      clearLocalStorageCropExplorerPseudoUserId();
      setPseudoUserId(null);
    }
  }, [
    handlePseudoCropExplorationsMigration,
    isAdmin,
    isCropExplorationsMigrationPending,
    isCropExplorationsMigrationSuccess,
    pseudoUserId,
    user,
    cropExplorations,
  ]);

  return (
    <>
      <Tabs
        dataTestId="crop-explorer-tabs"
        className="crop-explorer tabs"
        items={tabItems}
        type="editable-card"
        size="large"
        activeKey={activeTab}
        onEdit={handleAddOrEditTab}
        onChange={onTabChange}
        moreIcon={<Icon iconName="tabler/chevron-down" />}
        hideAdd={hideAddTabButton}
      />
      <Form form={form}>
        <Modal
          dataTestId="crop-explorer-add-project-modal"
          title={t("project.addProjectModal.title")}
          okText={t("project.addProjectModal.confirm")}
          cancelText={t("project.addProjectModal.cancel")}
          onOk={() => handleAddCropExploration(!!isAdmin)}
          loading={isPendingAddCropExploration}
          showModal={showAddProjectModal}
          onCancel={() => setShowAddProjectModal(false)}
          destroyOnHidden={true}
          okButtonProps={{ disabled: newName?.length === 0, "data-testid": "crop-explorer-add-project-modal-confirm" }}
          input={{
            initialValue: newName,
            label: t("project.addProjectModal.inputLabel"),
            onChange: (e) => setNewName(e.target.value),
            name: "add-name",
            rules: [{ required: true }],
            dataTestId: "add-name-input",
          }}
        />
      </Form>
      <OnboardingModal
        isOpen={showOnboardingModal}
        setIsOpen={setShowOnboardingModal}
        onFinish={handleOnboardingModalFinish}
        afterClose={launchOnboardingTour}
        isPending={isPendingAddCropExploration}
        isOnboarded={!!cropExplorerOnboardedLocalStorage}
      />
      <Tour
        arrow={true}
        mask={false}
        placement="bottom"
        open={openOnboardingTour}
        onClose={handleCloseOnboardingTour}
        steps={steps}
        indicatorsRender={(current, total) => {
          if (current === 0) return;
          return (
            <span>
              {t("tour.step")} {current}/{total - 1}
            </span>
          );
        }}
      />
    </>
  );
};
